import { api as defaultApi, ApiResponse, Token } from './api.js';
import { TableHeader } from '../admin/view/view.js';

export type ListView<T> = {
    [key in keyof T]: {
        original: T[key];
        view: string | null;
    };
};
export type CompleteEntityData = {
    general: Record<string, unknown>,
    langs: Record<string, Record<string, unknown>>,
    misc: { revisionComment?: string|null }
};
export type AdminResourceResponse = CompleteEntityData & {results: { id: number, general: any, langs: any } };
export type SortingData = {
    sorting: string[],
    mapping?: Record<string, any> & { sorting_field: string }
};
export type SortingResponse = { saved: Record<number, any> };

export interface AdminResourceApi<T> {
    url: string;
    resource: string;
    get(lang?: string | null, params?: URLSearchParams | null): Promise<ApiResponse<ListView<T>[]>>;
    getFiltered(filter: Record<string, unknown>, params?: URLSearchParams | null, idList?: number[]): Promise<ApiResponse<ListView<T>[]>>;
    saveSorting(data: SortingData): Promise<ApiResponse<SortingResponse>>;
    saveCompleteEntity(data: CompleteEntityData): Promise<ApiResponse<AdminResourceResponse>>;
}
export interface ResourceApi<T> {
    url: string;
    resource: string;
    fields(): Promise<ApiResponse<TableHeader>>;

    get(lang: string | null): Promise<ApiResponse<T[]>>;

    getFiltered(filter: Partial<T>, idList?: number[]): Promise<ApiResponse<T[]>>;

    getByID(id: number, lang: string | null): Promise<ApiResponse<T>>;

    getBy(field: string, value: any, lang: string | null): Promise<ApiResponse<T[]>>;

    getAutocomplete(
        field: string,
        value: any,
        lang: string | null,
    ): Promise<ApiResponse>;

    post<U = void>(data: Partial<T>): Promise<ApiResponse<U extends void ? T : U>>;

    put<U = void>(data: any): Promise<ApiResponse<U extends void ? T : U>>;

    patch<U = void>(data: Partial<T>): Promise<ApiResponse<U extends void ? T : U>>;

    putLanguage(lang: string, data: Partial<T>): Promise<ApiResponse>;

    patchLanguage(lang: string, data: Partial<T>): Promise<ApiResponse>;

    delete(id: number): Promise<ApiResponse>;

    abortRunningRequest(): void;
}

export interface UploadApi {
    file(input: HTMLInputElement, path: string): Promise<ApiResponse>;
}

export interface MetaApi {
    languages(): Promise<ApiResponse<string[]>>;

    resources(): Promise<ApiResponse<Record<string, string|null>>>;
}

export interface WidgetGroupApi {
    post(data: Record<string, unknown>): Promise<ApiResponse>;

    putLanguage(lang: string, data: Record<string, unknown>): Promise<ApiResponse>;

    postBulk(data: Record<number, unknown>): Promise<ApiResponse>;

    putLanguageBulk(lang: string, data: Record<number, unknown>): Promise<ApiResponse<{[key: number]: number}>>;
}

export interface BulkApi {
    insert(data: Record<string, unknown>): Promise<ApiResponse | Response>;
    update(data: Record<string, unknown>): Promise<ApiResponse | Response>;
    delete(conditionField: string, conditionId: number): Promise<ApiResponse | Response>;
}

export interface FormApi {
    send(form: string, data: Record<string, unknown>): Promise<ApiResponse>;
}

export type ApiClient = {
    meta: MetaApi;
    upload: UploadApi;
    resource: <T>(resource: string) => ResourceApi<T>;
    adminResource: <T>(resource: string) => AdminResourceApi<T>;
    form: FormApi;
    widget_group: (pwgid: number) => WidgetGroupApi;
    bulk: (resource: string) => BulkApi;
}

export const ApiClientFactory = ((basePath: string|null = window.baseUrl, api = defaultApi): ApiClient => {
    class AdminResource<T = void> implements AdminResourceApi<T> {
        public url: string;
        public readonly resource: string;

        constructor(url: string, resource: string) {
            this.url = url;
            this.resource = resource;
        }

        async get(lang: string = null, params: URLSearchParams = null): Promise<ApiResponse<ListView<T>[]>> {
            return api.api_get<ListView<T>[]>(`${this.url}/api/admin_resource/${this.resource}?${params?.toString()}`, lang);
        }

        async getFiltered(filter: Record<string, unknown>, params?: URLSearchParams | null, idList: number[] = []): Promise<ApiResponse<ListView<T>[]>> {
            if (idList.length > 0) filter.__idList = idList;
            return api.api_post<ListView<T>[]>(`${this.url}/api/admin_resource/${this.resource}?${params?.toString()}`, filter);
        }

        async saveSorting(data: SortingData): Promise<ApiResponse<SortingResponse>> {
            return api.api_put<SortingResponse>(`${this.url}/api/admin_resource/${this.resource}/sorting`, data);
        }

        async saveCompleteEntity(data: CompleteEntityData): Promise<ApiResponse<AdminResourceResponse>> {
            return api.api_post<AdminResourceResponse>(`${this.url}/api/admin_resource/${this.resource}/save`, data);
        }
    }

    /**
     * resource api calls
     * meant to cover about 95% of our needs
     */
    class Resource<T = void> implements ResourceApi<T> {
        public url: string;
        public readonly resource: string;

        constructor(url: string, resource: string) {
            this.url = url;
            this.resource = resource;
        }

        async fields(): Promise<ApiResponse<TableHeader>> {
            return api.api_get<TableHeader>(`${this.url}/api/resources/${this.resource}/fields`);
        }

        async get(lang: string|null = null): Promise<ApiResponse<T[]>> {
            return api.api_get<T[]>(`${this.url}/api/resources/${this.resource}`, lang);
        }

        async getFiltered(filter: Record<string, unknown>, idList: number[] = []): Promise<ApiResponse<T[]>> {
            if (idList.length > 0) filter.__idList = idList;
            return api.api_post<T[]>(`${this.url}/api/resources/${this.resource}/filter`, filter);
        }

        async getByID(id: number, lang: string|null = null): Promise<ApiResponse<T>> {
            return api.api_get<T>(`${this.url}/api/resources/${this.resource}/${id}`, lang);
        }

        async getBy(field: string, value: any, lang: string|null = null): Promise<ApiResponse<T[]>> {
            return api.api_get<T[]>(`${this.url}/api/resources/${this.resource}/${field}/${value}`, lang);
        }

        async getAutocomplete(
            field: string,
            value: any,
            lang: string|null = null,
        ): Promise<ApiResponse> {
            return api.api_get(`${this.url}/api/resources_autocomplete/${this.resource}/${field}/${value}`, lang);
        }

        async post<U = void>(data: Record<string, unknown>): Promise<ApiResponse<U extends void ? T : U>> {
            return api.api_post<U extends void ? T : U>(`${this.url}/api/resources/${this.resource}`, data);
        }

        async put<U = void>(data: Record<string, unknown>): Promise<ApiResponse<U extends void ? T : U>> {
            if (data.hasOwnProperty('id')) {
                return api.api_put<U extends void ? T : U>(`${this.url}/api/resources/${this.resource}/${data.id}`, data, null);
            }
            throw new Error('data must have an id, use post to add new data first');
        }

        async patch<U = void>(data: Record<string, unknown>): Promise<ApiResponse<U extends void ? T : U>> {
            if (data.hasOwnProperty('id')) {
                return api.api_patch<U extends void ? T : U>(`${this.url}/api/resources/${this.resource}/${data.id}`, data, null);
            }
            throw new Error('data must have an id, use post to add new data first');
        }

        async putLanguage(lang: string, data: Record<string, unknown>): Promise<ApiResponse> {
            if (data.hasOwnProperty('id') && data.id !== null) {
                return api.api_put(`${this.url}/api/resources/${this.resource}/${data.id}/lang`, data, lang);
            }
            throw new Error('data must have an id');
        }

        async patchLanguage(lang: string, data: Record<string, unknown>): Promise<ApiResponse> {
            if (data.hasOwnProperty('id') && data.id !== null) {
                return api.api_patch(`${this.url}/api/resources/${this.resource}/${data.id}/lang`, data, lang);
            }
            throw new Error('data must have an id');
        }

        async delete(id: number): Promise<ApiResponse> {
            return api.api_delete(`${this.url}/api/resources/${this.resource}/${id}`);
        }

        // enable aborting a running api request
        abortRunningRequest = api.abort_request.bind(api);
    }

    /**
     * UploadApi is used by the FileUploadWidget
     */
    class Upload implements UploadApi {
        private url: string;

        constructor(url: string) {
            this.url = url;
        }

        /**
         * @param input - the file input
         * @param path - the path to upload the file to, from the project root without leading and trailing slashes
         */
        file(input: HTMLInputElement, path: string): Promise<ApiResponse> {
            const data = new FormData();
            data.append('file', input.files.item(0));
            data.append('path', path);

            // does not use api.post() due to content-type form/multipart header added
            // by the browser is not compatible with content-type application/json
            return api.request_token().then((t: Token) => t.token).then((token: string) => {
                const headers = new Headers({
                    Authorization: `Bearer ${token}`,
                });

                return fetch(`${this.url}/api/upload`, {
                    body: data,
                    method: 'post',
                    headers,
                })
                    .then((res: Response) => res.json())
                    .then((res: ApiResponse) => {
                        if (res.statusCode.toString().search(/2\d\d/) === -1) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
            });
        }
    }

    /**
     * todo: list usages in comment
     */
    class Meta implements MetaApi {
        private readonly url: string;

        constructor(url: string) {
            this.url = url;
        }

        async languages(): Promise<ApiResponse<string[]>> {
            return api.api_get<string[]>(`${this.url}/api/languages`);
        }

        async resources(): Promise<ApiResponse<Record<string, string|null>>> {
            return api.api_get<Record<string, string|null>>(`${this.url}/api/resources`);
        }
    }

    /**
     * Used by the frontend form class to send form data
     */
    class Form implements FormApi {
        private readonly url: string;

        constructor(url: string) {
            this.url = url;
        }

        async send(form: string, data: Record<string, unknown>): Promise<ApiResponse> {
            return api.api_post(`${this.url}/api/form/${form}`, data);
        }
    }

    class WidgetGroup implements WidgetGroupApi {
        private url: string;

        private readonly pwgid: number;

        constructor(url: string, pwgid: number) {
            this.url = url;
            this.pwgid = pwgid;
        }

        async post(data: Record<string, unknown>): Promise<ApiResponse> {
            return api.api_post(`${this.url}/api/widget_group/${this.pwgid}`, data);
        }

        async putLanguage(lang: string, data: Record<string, unknown>): Promise<ApiResponse> {
            return api.api_put(`${this.url}/api/widget_group/${this.pwgid}`, data, lang);
        }

        async postBulk(data: Record<number, unknown>): Promise<ApiResponse> {
            return api.api_post(`${this.url}/api/widget_group/bulk`, data);
        }

        async putLanguageBulk(lang: string, data: Record<number, unknown>): Promise<ApiResponse<{[key: number]: number}>> {
            return api.api_put<{[key: number]: number}>(`${this.url}/api/widget_group/bulk`, data, lang);
        }
    }

    class Bulk<T> implements BulkApi {
        private url: string;

        private readonly resource: string;

        /**
         * Bulk constructor
         *          *          */
        constructor(url: string, resource: string) {
            this.url = url;
            this.resource = resource;
        }

        /**
         * bulk inserts
         */
        async insert(data: Record<string, unknown>): Promise<ApiResponse|Response> {
            // does not use api.post() because we need the bulk insert endpoint
            return api.request_token().then((t: Token) => t.token).then((token: string) => {
                const headers = new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                });

                return fetch(`${this.url}/api/bulk/${this.resource}`, {
                    body: JSON.stringify(data),
                    method: 'post',
                    headers,
                });
            });
        }

        /**
         * bulk updates
         * @param data - array of updates
         */
        async update(data: Record<string, unknown>): Promise<ApiResponse|Response> {
            // does not use api.put() because we need the bulk update endpoint
            return api.request_token().then((t: Token) => t.token).then((token: string) => {
                const headers = new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                });

                return fetch(`${this.url}/api/bulk/${this.resource}`, {
                    body: JSON.stringify(data),
                    method: 'put',
                    headers,
                });
            });
        }

        /**
         * bulk deletion
         */
        async delete(conditionField: string, conditionId: number): Promise<ApiResponse|Response> {
            // does not use api.delete() because we need the bulk delete endpoint
            return api.request_token().then((t: Token) => t.token).then((token: string) => {
                const headers = new Headers({
                    Authorization: `Bearer ${token}`,
                });

                return fetch(`${this.url}/api/bulk/${this.resource}/${conditionField}/${conditionId}`, {
                    method: 'delete',
                    headers,
                });
            });
        }
    }

    let url: string = basePath || `/${document.location.hostname}`;

    // remove trailing slash
    if (url.split('').reverse()[0] === '/') {
        const tempUrl = url.split('');
        tempUrl.pop();
        url = tempUrl.join('');
    }

    return {
        meta: new Meta(url),
        upload: new Upload(url),
        resource: <T>(resource: string): Resource<T> => new Resource<T>(url, resource),
        adminResource: <T>(resource: string): AdminResource<T> => new AdminResource<T>(url, resource),
        form: new Form(url),
        widget_group: (pwgid: number): WidgetGroup => new WidgetGroup(url, pwgid),
        bulk: <T>(resource: string): Bulk<T> => new Bulk<T>(url, resource),
    };
});
